import { Col, Row } from "antd";
import React, { useState, ReactElement, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  DocumentCategories,
  IProjectAttachment,
  IProjectRouteParams,
} from "src/pages/projects/project/interfaces";
import { AttachmentType } from "src/constants";
import MobileDrawer from "src/components/mobile-drawer";
import { useParams } from "react-router-dom";
import FileUploader from "src/components/file-upload";
import useAuth from "src/auth/use-auth";
import { checkFilesCurrentlyUploading } from "src/components/file-upload/helpers";
import { cloneDeep } from "lodash";
import { getIsMobile } from "@songtradr/spa-common";
import { IDeleteSelectedFile } from "src/components/file-upload/interfaces";
import { IProjectUploadFile } from "../../../details/components/interfaces";
import style from "../../../../styles";
import { FileDrawerTypes, generateDrawer } from "../mobile-drawers";
import { DocumentsTableColumns, DocumentsTableData } from "../../table-data";

interface IProps {
  attachments: IProjectAttachment[];
}

const Invoices = ({ attachments }: IProps): ReactElement => {
  const { t } = useTranslation();

  const { getAccessToken, organisationId } = useAuth();
  const [accessToken, setAccessToken] = useState("");

  const [invoicesFileList, setInvoicesFileList] = useState<
    IProjectUploadFile[]
  >([]);

  const handleSetInvoicesFileList = useCallback(
    (files: IProjectUploadFile[]) => {
      setInvoicesFileList(files);
    },
    []
  );

  const [
    selectedFileToDelete,
    setSelectedFileToDelete,
  ] = useState<IDeleteSelectedFile>();
  const handleSetSelectedFileToDelete = useCallback(
    (file: IDeleteSelectedFile | undefined) => {
      setSelectedFileToDelete(file);
    },
    []
  );

  const [activeFile, setActiveFile] = useState<IProjectUploadFile>();
  const handleSetActiveFile = useCallback(
    (file: IProjectUploadFile | undefined) => {
      setActiveFile(file);
    },
    []
  );

  const [showFileUploadDeleteModal, setShowFileUploadDeleteModal] = useState(
    false
  );
  const handleShowFileUploadDeleteModal = useCallback((show: boolean) => {
    setShowFileUploadDeleteModal(show);
  }, []);

  const [isDrawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [activeDrawer, setActiveDrawer] = useState<FileDrawerTypes>(
    FileDrawerTypes.FileInfoDrawer
  );
  const handleSetActiveDrawer = useCallback((drawer: FileDrawerTypes) => {
    setActiveDrawer(drawer);
  }, []);

  const isMobile = getIsMobile();
  const { id: projectId } = useParams<IProjectRouteParams>();

  const toggleDrawer = useCallback(() => {
    setDrawerVisible((state) => !state);
  }, []);

  useEffect(() => {
    if (!checkFilesCurrentlyUploading(invoicesFileList)) {
      setInvoicesFileList(cloneDeep(attachments));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  useEffect(() => {
    void (() => {
      const userAccessToken: string = getAccessToken();
      setAccessToken(userAccessToken);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Row role="row" gutter={[8, 8]}>
        <Col span={24}>
          <div css={style.documentsUploadContainer}>
            <FileUploader
              attachmentType={AttachmentType.Documents}
              documentCategory={DocumentCategories.Invoice}
              invalidFileSizeErrorMessage={t(
                "File size exceeds maximum limit of 2GB"
              )}
              existingFiles={invoicesFileList}
              setFileList={handleSetInvoicesFileList}
              tableData={DocumentsTableData({
                filesList: invoicesFileList,
                accessToken,
                projectId,
                organisationId,
                setShowModal: handleShowFileUploadDeleteModal,
                setSelectedFileToDelete: handleSetSelectedFileToDelete,
                toggleDrawer,
                setActiveDrawer: handleSetActiveDrawer,
                setActiveFile: handleSetActiveFile,
              })}
              selectedFileToDelete={selectedFileToDelete}
              tableColumns={DocumentsTableColumns(isMobile, t)}
              showModal={showFileUploadDeleteModal}
              setShowModal={handleShowFileUploadDeleteModal}
              additionalContainerStyles={style.documentsFileUploader}
            />
          </div>
        </Col>
      </Row>
      {isMobile && (
        <MobileDrawer
          placement="bottom"
          onClose={toggleDrawer}
          open={isDrawerVisible}
          key="bottom"
          closable={false}
          data-testid="mobile-drawer-container"
          className="documents-drawer-container"
        >
          {generateDrawer(
            activeDrawer,
            handleSetActiveDrawer,
            toggleDrawer,
            accessToken,
            projectId,
            organisationId,
            handleSetInvoicesFileList,
            invoicesFileList,
            handleShowFileUploadDeleteModal,
            selectedFileToDelete,
            activeFile
          )}
        </MobileDrawer>
      )}
    </div>
  );
};

export default Invoices;
